import type { T_ChatV8UpcomingEventAddToCalendar } from "@lattice/core-lib/types/chat-v8";

/**
 * Dependency-free calendar handoff rules (RAI-1097), shared by both
 * platforms so the same wire payload always produces the same event.
 *
 * Google gets the documented render URL template; Apple gets a standards-
 * compliant VCALENDAR blob (web downloads it, iOS offers it to Calendar via
 * the share sheet). Neither needs a native calendar module or OAuth.
 */

const DEFAULT_EVENT_DURATION_MS = 60 * 60 * 1000;

/** 20260805T200000Z — the compact UTC form both formats require. */
function toCalendarUtcStamp(iso: string): string | null {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return null;
  return `${parsed.toISOString().replace(/[-:]/g, "").slice(0, 15)}Z`;
}

/** 20260805 — all-day events use date-only stamps. */
function toCalendarDateStamp(iso: string): string | null {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString().replace(/-/g, "").slice(0, 8);
}

export function buildGoogleCalendarUrl(
  event: T_ChatV8UpcomingEventAddToCalendar,
): string | null {
  const start = new Date(event.startsAt);
  if (Number.isNaN(start.getTime())) return null;

  let dates: string;
  if (event.allDay) {
    const startStamp = toCalendarDateStamp(event.startsAt);
    const endStamp = toCalendarDateStamp(
      new Date(start.getTime() + 24 * 60 * 60 * 1000).toISOString(),
    );
    if (startStamp === null || endStamp === null) return null;
    dates = `${startStamp}/${endStamp}`;
  } else {
    const startStamp = toCalendarUtcStamp(event.startsAt);
    const endStamp = toCalendarUtcStamp(
      new Date(start.getTime() + DEFAULT_EVENT_DURATION_MS).toISOString(),
    );
    if (startStamp === null || endStamp === null) return null;
    dates = `${startStamp}/${endStamp}`;
  }

  const params = new URLSearchParams({
    action: "TEMPLATE",
    dates,
    text: event.eventName,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/** RFC 5545 requires escaping backslash, semicolon, comma, and newlines. */
function escapeIcsText(value: string): string {
  return value
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r?\n/g, "\\n");
}

export function buildIcsContent(
  event: T_ChatV8UpcomingEventAddToCalendar,
): string | null {
  const start = new Date(event.startsAt);
  if (Number.isNaN(start.getTime())) return null;

  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Lattice//Chat//EN",
    "BEGIN:VEVENT",
    `UID:${escapeIcsText(event.sourceEventId)}@lattice`,
  ];
  if (event.allDay) {
    const stamp = toCalendarDateStamp(event.startsAt);
    if (stamp === null) return null;
    lines.push(`DTSTART;VALUE=DATE:${stamp}`);
  } else {
    const startStamp = toCalendarUtcStamp(event.startsAt);
    const endStamp = toCalendarUtcStamp(
      new Date(start.getTime() + DEFAULT_EVENT_DURATION_MS).toISOString(),
    );
    if (startStamp === null || endStamp === null) return null;
    lines.push(`DTSTART:${startStamp}`, `DTEND:${endStamp}`);
  }
  lines.push(
    `SUMMARY:${escapeIcsText(event.eventName)}`,
    "END:VEVENT",
    "END:VCALENDAR",
  );
  return lines.join("\r\n");
}
